import { createFileRoute } from '@tanstack/react-router';
import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { DashboardWidget } from '@/components/DashboardWidget';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { apiFetch } from '@/utils/api';
import { PlusIcon, SaveIcon, LayoutGridIcon } from 'lucide-react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { useI18n } from '@/hooks/useI18n';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import '../styles/dashboard.css';
const ResponsiveGridLayout = WidthProvider(Responsive);


interface Widget {
  id: string;
  title: string;
  description?: string;
  metric_id: string;
  visualization_type: 'table' | 'bar' | 'line';
  width: number;
  position_x: number;
  position_y: number;
  height: number;
  show_legend: boolean;
  show_grid: boolean;
  color_scheme?: string;
  dimensions?: string[];
  filters?: Record<string, any>;
  time_range_type?: string;
  sort_by?: string;
  sort_order?: string;
  limit?: number;
}

interface Metric {
  id: string;
  metric_code: string;
  display_name: string;
  description?: string;
  category?: string;
  supported_dimensions?: string[];
}

interface Dimension {
  id: string;
  dimension_code: string;
  display_name: string;
  description?: string;
  dimension_type: string;
}

function DashboardPage() {
  const { t } = useI18n();
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [dimensions, setDimensions] = useState<Dimension[]>([]);
  const [tags, setTags] = useState<{ id: string; name: string; color?: string | null }[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingWidget, setEditingWidget] = useState<Widget | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    metric_id: '',
    visualization_type: 'table' as 'table' | 'bar' | 'line',
    width: 1,
    show_legend: true,
    show_grid: true,
    dimensions: [] as string[],
    time_range_type: 'last_30_days',
    sort_by: '' as string,
    sort_order: 'asc',
    limit: null as number | null,
    filters: {} as Record<string, any>,
  });

  useEffect(() => {
    fetchWidgets();
    fetchMetrics();
    fetchDimensions();
  }, []);

  const fetchWidgets = async () => {
    try {
      setLoading(true);
      const response = await apiFetch('/api/dashboard/widgets?is_active=true');
      if (response.ok) {
        const data = await response.json();
        setWidgets(data);
      }
    } catch (error) {
      console.error('Error fetching widgets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMetrics = async () => {
    try {
      const response = await apiFetch('/api/metrics/definitions?is_active=true');
      if (response.ok) {
        const data = await response.json();
        setMetrics(data);
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
    }
  };

  const fetchDimensions = async () => {
    try {
      const response = await apiFetch('/api/metrics/dimensions?is_active=true');
      if (response.ok) {
        const data = await response.json();
        setDimensions(data);
      }
    } catch (error) {
      console.error('Error fetching dimensions:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const res = await apiFetch('/api/tags');
      if (res.ok) {
        const data = await res.json();
        setTags(data);
      }
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  };

  const handleAddWidget = () => {
    setEditingWidget(null);
    setFormData({
      title: '',
      description: '',
      metric_id: '',
      visualization_type: 'table',
      width: 1,
      show_legend: true,
      show_grid: true,
      dimensions: [],
      time_range_type: 'last_30_days',
      sort_by: '',
      sort_order: 'asc',
      limit: null,
      filters: {},
    });
    setShowAddDialog(true);
    fetchTags();
  };

  const handleEditWidget = (widgetId: string) => {
    const widget = widgets.find((w) => w.id === widgetId);
    if (widget) {
      setEditingWidget(widget);
      setFormData({
        title: widget.title,
        description: widget.description || '',
        metric_id: widget.metric_id,
        visualization_type: widget.visualization_type,
        width: widget.width,
        show_legend: widget.show_legend,
        show_grid: widget.show_grid,
        dimensions: widget.dimensions || [],
        time_range_type: widget.time_range_type || 'last_30_days',
        sort_by: widget.sort_by || '',
        sort_order: widget.sort_order || 'asc',
        limit: widget.limit || null,
        filters: widget.filters || {},
      });
      setShowAddDialog(true);
      fetchTags();
    }
  };

  const handleDeleteWidget = async (widgetId: string) => {
    if (!confirm(t('dashboard.confirmDelete', 'common'))) {
      return;
    }

    try {
      const response = await apiFetch(`/api/dashboard/widgets/${widgetId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setWidgets(widgets.filter((w) => w.id !== widgetId));
      }
    } catch (error) {
      console.error('Error deleting widget:', error);
    }
  };

  const handleSaveWidget = async () => {
    try {
      const url = editingWidget
        ? `/api/dashboard/widgets/${editingWidget.id}`
        : '/api/dashboard/widgets';

      const method = editingWidget ? 'PATCH' : 'POST';

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        await fetchWidgets();
        setShowAddDialog(false);
      }
    } catch (error) {
      console.error('Error saving widget:', error);
    }
  };

  const handleLayoutChange = async (layout: any[]) => {
    // Prepare bulk update
    const updates = layout.map((item) => ({
      widget_id: item.i,
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h,
    }));

    try {
      await apiFetch('/api/dashboard/widgets/bulk-update-positions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
    } catch (error) {
      console.error('Error updating layout:', error);
    }
  };

  const handleResponsiveLayoutChange = (currentLayout: any[], allLayouts: any) => {
    const lgLayout = allLayouts?.lg;
    handleLayoutChange(lgLayout && lgLayout.length ? lgLayout : currentLayout);
  };

  // Convert widgets to grid layout format
  const layout = widgets.map((widget) => ({
    i: widget.id,
    x: widget.position_x,
    y: widget.position_y,
    w: widget.width,
    h: widget.height,
    minW: 1,
    maxW: 3,
    minH: 2,
    maxH: 20,
  }));

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t('navigation.dashboard', 'common')}</h1>
            <p className="text-muted-foreground">
              {t('dashboard.subtitle', 'common')}
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAddWidget}>
              <PlusIcon className="h-4 w-4 mr-2" />
              {t('dashboard.addWidget', 'common')}
            </Button>
          </div>
        </div>

        {/* Grid Layout */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-muted-foreground">{t('dashboard.loading', 'common')}</p>
          </div>
        ) : widgets.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 space-y-4">
            <LayoutGridIcon className="h-16 w-16 text-muted-foreground" />
            <p className="text-muted-foreground">{t('dashboard.noWidgets', 'common')}</p>
            <Button onClick={handleAddWidget}>
              <PlusIcon className="h-4 w-4 mr-2" />
              {t('dashboard.addFirstWidget', 'common')}
            </Button>
          </div>
        ) : (
          <ResponsiveGridLayout
            className="layout"
            layouts={{ lg: layout }}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 3, md: 3, sm: 1, xs: 1, xxs: 1 }}
            rowHeight={100}
            onLayoutChange={handleResponsiveLayoutChange}
            draggableHandle=".drag-handle"
            compactType="vertical"
          >
            {widgets.map((widget) => (
              <div key={widget.id}>
                <DashboardWidget
                  widget={widget}
                  onEdit={handleEditWidget}
                  onDelete={handleDeleteWidget}
                />
              </div>
            ))}
          </ResponsiveGridLayout>
        )}

        {/* Add/Edit Widget Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingWidget ? t('dashboard.editWidget', 'common') : t('dashboard.addWidget', 'common')}
              </DialogTitle>
              <DialogDescription>
                {t('dashboard.configureWidget', 'common')}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="title">{t('forms.title', 'common')}</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder={t('dashboard.widgetTitlePlaceholder', 'common')}
                />
              </div>

              <div>
                <Label htmlFor="description">{t('dashboard.descriptionOptional', 'common')}</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('dashboard.widgetDescriptionPlaceholder', 'common')}
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor="metric">{t('dashboard.metric', 'common')}</Label>
                <Select
                  value={formData.metric_id}
                  onValueChange={(value) => setFormData({ ...formData, metric_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('dashboard.selectMetric', 'common')} />
                  </SelectTrigger>
                  <SelectContent>
                    {metrics.map((metric) => (
                      <SelectItem key={metric.id} value={metric.id}>
                        {metric.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="visualization">{t('dashboard.visualizationType', 'common')}</Label>
                  <Select
                    value={formData.visualization_type}
                    onValueChange={(value: any) => setFormData({ ...formData, visualization_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="table">{t('dashboard.table', 'common')}</SelectItem>
                      <SelectItem value="bar">{t('dashboard.barChart', 'common')}</SelectItem>
                      <SelectItem value="line">{t('dashboard.lineChart', 'common')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="width">{t('dashboard.widthColumns', 'common')}</Label>
                  <Select
                    value={formData.width.toString()}
                    onValueChange={(value) => setFormData({ ...formData, width: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">{t('dashboard.oneColumn', 'common')}</SelectItem>
                      <SelectItem value="2">{t('dashboard.twoColumns', 'common')}</SelectItem>
                      <SelectItem value="3">{t('dashboard.threeColumns', 'common')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Dimensions Selection */}
              <div>
                <Label>{t('dashboard.dimensionsOptional', 'common')}</Label>
                <div className="grid grid-cols-2 gap-3 p-4 border rounded-lg bg-gray-50 max-h-48 overflow-y-auto">
                  {(() => {
                    const selectedMetric = metrics.find(m => m.id === formData.metric_id);
                    const availableDimensions = selectedMetric?.supported_dimensions && selectedMetric.supported_dimensions.length > 0
                      ? dimensions.filter(dim => selectedMetric.supported_dimensions?.includes(dim.dimension_code))
                      : dimensions;

                    if (availableDimensions.length === 0) {
                      return (
                        <div className="col-span-2 text-sm text-gray-500">
                          {formData.metric_id ? t('dashboard.noDimensionsAvailable', 'common') : t('dashboard.selectMetricFirst', 'common')}
                        </div>
                      );
                    }

                    return availableDimensions.map((dim) => (
                      <div key={dim.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`dim-${dim.dimension_code}`}
                          checked={formData.dimensions.includes(dim.dimension_code)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({
                                ...formData,
                                dimensions: [...formData.dimensions, dim.dimension_code]
                              });
                            } else {


                              setFormData({
                                ...formData,
                                dimensions: formData.dimensions.filter(d => d !== dim.dimension_code)
                              });
                            }
                          }}
                        />
                        <label
                          htmlFor={`dim-${dim.dimension_code}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {dim.display_name}
                        </label>
                      </div>
                    ));
                  })()}
                </div>
              </div>

              {/* Sort By and Sort Order */}
              {formData.dimensions.length > 0 && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sort_by">{t('dashboard.sortBy', 'common')}</Label>
                    <Select
                      value={formData.sort_by || formData.dimensions[0]}
                      onValueChange={(value) => setFormData({ ...formData, sort_by: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {formData.dimensions.map((dimCode) => {
                          const dim = dimensions.find(d => d.dimension_code === dimCode);
                          return (
                            <SelectItem key={dimCode} value={dimCode}>
                              {dim?.display_name || dimCode}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="sort_order">{t('dashboard.sortOrder', 'common')}</Label>
                    <Select
                      value={formData.sort_order}
                      onValueChange={(value) => setFormData({ ...formData, sort_order: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asc">{t('dashboard.ascending', 'common')}</SelectItem>
                        <SelectItem value="desc">{t('dashboard.descending', 'common')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Time Range */}

              {/* Tag Filter (optional, shown only if metric supports Tag dimension) */}
              <div className="mt-4">
                <Label>{t('dashboard.tagFilterOptional', 'common')}</Label>
                {(() => {
                  const selectedMetric = metrics.find(m => m.id === formData.metric_id);
                  const supportsTag = selectedMetric?.supported_dimensions?.includes('tag');
                  if (!supportsTag) return null;

                  const selectedTagIds: string[] = (formData.filters?.tag_ids || []) as string[];

                  return (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label>{t('dashboard.tagMatch', 'common')}</Label>
                        <Select
                          value={(formData.filters?.tag_match_mode as string) || 'OR'}
                          onValueChange={(value) => setFormData({
                            ...formData,
                            filters: { ...(formData.filters || {}), tag_match_mode: value }
                          })}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="OR">{t('dashboard.anyOr', 'common')}</SelectItem>
                            <SelectItem value="AND">{t('dashboard.allAnd', 'common')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-2 gap-3 p-4 border rounded-lg bg-gray-50 max-h-40 overflow-y-auto">
                        {tags.length === 0 ? (
                          <div className="col-span-2 text-sm text-gray-500">{t('dashboard.noTags', 'common')}</div>
                        ) : (
                          tags.map((tag) => (
                            <div key={tag.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`tag-${tag.id}`}
                                checked={selectedTagIds.includes(tag.id)}
                                onCheckedChange={(checked) => {
                                  const current = selectedTagIds;
                                  const next = checked
                                    ? [...current, tag.id]
                                    : current.filter((id) => id !== tag.id);
                                  setFormData({
                                    ...formData,
                                    filters: { ...(formData.filters || {}), tag_ids: next },
                                  });
                                }}
                              />
                              <label
                                htmlFor={`tag-${tag.id}`}
                                className="text-sm leading-none cursor-pointer"
                              >
                                {tag.name}
                              </label>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div>
                <Label htmlFor="time_range">{t('dashboard.timeRange', 'common')}</Label>
                <Select
                  value={formData.time_range_type}
                  onValueChange={(value) => setFormData({ ...formData, time_range_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="last_7_days">{t('dashboard.last7Days', 'common')}</SelectItem>
                    <SelectItem value="last_30_days">{t('dashboard.last30Days', 'common')}</SelectItem>
                    <SelectItem value="last_90_days">{t('dashboard.last90Days', 'common')}</SelectItem>
                    <SelectItem value="last_12_months">{t('dashboard.last12Months', 'common')}</SelectItem>
                    <SelectItem value="this_month">{t('dashboard.thisMonth', 'common')}</SelectItem>
                    <SelectItem value="this_year">{t('dashboard.thisYear', 'common')}</SelectItem>
                    <SelectItem value="all_time">{t('dashboard.allTime', 'common')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                {t('actions.cancel', 'common')}
              </Button>
              <Button onClick={handleSaveWidget}>
                <SaveIcon className="h-4 w-4 mr-2" />
                {editingWidget ? t('actions.update', 'common') : t('actions.create', 'common')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export const Route = createFileRoute('/dashboard')({
  component: DashboardPage,
});
